import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { PopulatedInstantFeedbackUserResponse } from '@libs/instant-feedback/config';
import { PaginationSortingQuery } from '@libs/nest-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from "primeng/button";
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { filter, firstValueFrom } from 'rxjs';
import { SvgComponent } from '../../../@core/components/svg/svg.component';
import { WCResponse } from '../../../@core/data/webcontents';
import { SuccessModalComponent } from '../../../components/success-modal/success-modal.component';
import { InstantFeedbackDialogComponent } from '../../../pages/instant-feedback-dialog/instant-feedback-dialog.component';
import { OpenFeedbackCreateDialog } from '../../../pages/open-feedback-dialog/open-feedback-create-dialog';
import { FeedbacksService } from '../../../services/feedback.service';
import { LayoutService } from '../../../services/layout.service';
import { WebcontentService } from '../../../services/webcontent.service';
import { AppMenuitem, CustomMenuItem } from '../app.menuitem';
import { AppSubMenu } from '../submenu/submenu.component';
import { ObserveSidebarDirective } from './sidebar.directive';

enum OrderBy {
  ASC = 'asc',
  DESC = 'desc',
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    ListboxModule,
    AppMenuitem,
    CommonModule,
    AppSubMenu,
    TranslateModule,
    DialogModule,
    OpenFeedbackCreateDialog,
    InstantFeedbackDialogComponent,
    SuccessModalComponent,
    RouterModule,
    SvgComponent,
    ButtonModule,
    ObserveSidebarDirective
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(-20px)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ transform: 'translateX(-20px)', opacity: 0 }))
      ])
    ])
  ]
})
export class AppSidebar implements OnInit, AfterViewInit {
  constructor(
    public el: ElementRef,
    private layoutService: LayoutService,
    private translate: TranslateService,
    private feedbacksService: FeedbacksService,
    protected router: Router,
    private webcontentService: WebcontentService,
  ) { }

  ngAfterViewInit(): void {
  }

  initialModel: CustomMenuItem[] = [];
  endModel: CustomMenuItem[] = [];

  // menuRoot: any[] = []; // livello iniziale
  selectedLevels: any[] = []; // livelli successivi
  contentsMenuItem: CustomMenuItem | null = null;
  // Percorso degli antenati della pagina corrente, da espandere nel submenu contenuti
  contentsExpandedPath: string[] = [];
  currentContentId: string | null = null;
  private navigationRequestId = 0;

  displayOpenFeedbackDialog: boolean = false;
  showSubmenu: boolean = false;
  showSuccessModal: boolean = false;
  showSuccessModalFromOpenFeedback: boolean = false;
  showSuccessModalFromInstantFeedback: boolean = false;
  instantFeedback = signal<string | undefined>(undefined);
  instantFeedbacksDetail = signal<PopulatedInstantFeedbackUserResponse | undefined>(undefined);
  instantFeedbackDelay = signal<number | undefined>(undefined);
  anchorBtnPx = '0px';

  paginationOpenFeedbackSortingQuery: PaginationSortingQuery = {
    page: 1,
    size: 1,
    getTotalCount: true,
    orderBy: OrderBy.DESC,
    sortBy: 'startDate',
  };

  onFeedbackSuccess() {
    // Delay to next event loop to ensure dialog is closed and DOM is updated
    setTimeout(() => {
      this.showSuccessModal = true;
    }, 0);
  }

  private feedbackDialogTimeout: any = null;

  // Variabili per capire quale dialog è aperto
  get isOpenFeedbackDialogOpen(): boolean {
    return !!this.displayOpenFeedbackDialog;
  }
  get isInstantFeedbackDialogOpen(): boolean {
    return !!this.instantFeedbacksDetail();
  }

  get dialogSuccessTitle(): string {
    if (this.showSuccessModalFromOpenFeedback) {
      return this.translate.instant('openFeedback.success.title.label');
    } else {
      return this.translate.instant('instantFeedback.success.title.label');
    }
  }

  get dialogSuccessMessage(): string {
    if (this.showSuccessModalFromOpenFeedback) {
      return this.translate.instant('openFeedback.success.message.label');
    }
    {
      return this.translate.instant('instantFeedback.success.message.label');
    }
  }

  get dialogSuccessClose(): string {
    if (this.showSuccessModalFromOpenFeedback) {
      return this.translate.instant('openFeedback.success.close.label');
    } else {
      return this.translate.instant('instantFeedback.success.close.label');
    }
  }

  async ngOnInit() {
    let webContentItems: CustomMenuItem[] = [];
    try {
      const webContentMenu = await firstValueFrom(this.layoutService.getWebContentMenu());
      const rootChildren: any[] = webContentMenu ?? [];
      webContentItems = rootChildren.map((element) => this.transformNode(element));
    } catch (error) {
      console.error('Failed to load web content menu:', error);
    }

    // Adjusted the paths for initialModel icons to ensure consistency with endModel
    this.initialModel = [
      { label: 'menu.home.label', icon: '/assets/svg/menu/home.svg', routerLink: ['/'] },
      { label: 'menu.board.label', icon: '/assets/svg/menu/board.svg', routerLink: ['/board'] },
      { label: 'menu.community.label', icon: '/assets/svg/menu/community.svg', routerLink: ['/community'] },
      { label: 'menu.events.label', icon: '/assets/svg/menu/event.svg', routerLink: ['/events'] },
      { label: 'menu.contest.label', icon: '/assets/svg/menu/contest.svg', routerLink: ['/contests'] },
      { label: 'menu.repository.label', icon: '/assets/svg/menu/repository.svg', routerLink: ['/repository'] },
      // { label: 'menu.chat.label', icon: '/assets/svg/menu/chat.svg', routerLink: ['/chats'] },
      { label: 'menu.onboarding.label', icon: '/assets/svg/menu/onboarding.svg', routerLink: ['/onboarding'] },
      // { label: 'menu.knowledge.label', icon: '/assets/svg/menu/knowledge.svg', routerLink: ['/knowledge'] },
    ];

    if (webContentItems.length > 0) {
      this.contentsMenuItem = {
        label: 'menu.contents.label',
        icon: '/assets/svg/menu/content.svg',
        kind: 'menu-item',
        items: webContentItems,
      };
      this.initialModel.splice(2, 0, this.contentsMenuItem);
    }

    this.endModel = [
      {
        kind: '',
        parentNode: '',
        items: [
          {
            label: 'menu.survey.label',
            icon: '/assets/svg/menu/survey.svg',
            routerLink: ['/surveys'],
          },
          {
            label: 'menu.feedback.label',
            icon: '/assets/svg/menu/feedback.svg',
            command: () => this.onOpenInstantFeedback(),
          },
        ],
      },
    ];
    this.getAllOpenFeedbacks();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const requestId = ++this.navigationRequestId;
      if (!event.urlAfterRedirects.includes('contents')) {
        this.showSubmenu = false;
      }
      if (!this.showSubmenu) {
        this.selectedLevels = [];
      }
      this.syncContentsPanel(event.urlAfterRedirects, requestId);
    });

    // La navigazione iniziale può essere già terminata mentre il menu veniva caricato
    this.syncContentsPanel(this.router.url, ++this.navigationRequestId);
  }

  // Su una pagina /contents/:identifier apre il menu contenuti espandendo il percorso fino alla pagina
  private async syncContentsPanel(url: string, requestId: number): Promise<void> {
    const identifier = this.getContentIdentifier(url);
    if (!identifier) {
      this.currentContentId = null;
      return;
    }

    const page = await this.resolveContentPage(identifier);
    if (requestId !== this.navigationRequestId || !page) {
      return;
    }

    this.currentContentId = page.id;
    this.contentsExpandedPath = await this.resolveAncestorIds(page.parent);

    if (requestId !== this.navigationRequestId) {
      return;
    }
    if (!this.showSubmenu && this.contentsMenuItem) {
      this.selectedLevels = [this.contentsMenuItem];
      this.showSubmenu = true;
    }
  }

  private getContentIdentifier(url: string): string | null {
    const match = /^\/contents\/([^/?#]+)/.exec(url);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private async resolveContentPage(identifier: string): Promise<WCResponse | null> {
    try {
      return await firstValueFrom(this.webcontentService.getBySlug(identifier));
    } catch {
      try {
        return await firstValueFrom(this.webcontentService.getById(identifier));
      } catch {
        return null;
      }
    }
  }

  // Ritorna gli id degli antenati dal livello radice fino al genitore diretto
  private async resolveAncestorIds(parentId: string | null): Promise<string[]> {
    const ancestorIds: string[] = [];
    let currentParentId = parentId;
    while (currentParentId && !ancestorIds.includes(currentParentId)) {
      ancestorIds.unshift(currentParentId);
      try {
        const parent = await firstValueFrom(this.webcontentService.getById(currentParentId));
        currentParentId = parent.parent;
      } catch {
        break;
      }
    }
    return ancestorIds;
  }

  onSelect(item: CustomMenuItem, level: number) {
    console.log('this.showSubmenu before:', this.showSubmenu);
    if (this.showSubmenu) {
      this.showSubmenu = false;
    } else {
      this.showSubmenu = true;
      // taglia i livelli successivi
      this.selectedLevels = this.selectedLevels.slice(0, level);

      // se ci sono figli, mostra il prossimo livello
      if (item?.items && item?.items.length > 0) {
        this.selectedLevels.push(item);
      }
    }
  }

  transformNode(node: any) {
    return {
      label: node.menuTitle,
      id: node.id,
      url: node.url,
      icon: '', // puoi impostare un'icona personalizzata qui
      kind: node.kind,
      slug: node.slug,
      parentNode: node.parent,
      hasChildren: node.hasChildren,
      items: [],
    };
  }

  // Open feedbacks loading
  getAllOpenFeedbacks() {
    this.feedbacksService.getLastInstantFeedbacks().subscribe({
      next: (response) => {
        // if (response.items && response.items.length > 0) {
        //     const id = response.items[0].id;
        //     this.instantFeedback.set(id);
        //     this.feedbacksService.getInstantFeedbackById(id).subscribe({
        //         next: (detail: InstantFeedbackFullResponse) => {
        this.instantFeedbacksDetail.set(response);
        // },
        // error: (err) => {
        //     this.instantFeedbacksDetail.set(undefined);
        //     console.error('Error fetching instant feedback detail:', err);
        // }
      },
      // ),
      // } else {
      //     this.instantFeedback.set(undefined);
      //     this.instantFeedbacksDetail.set(undefined);
      // }
      // },
      error: (error: any) => {
        console.error('Error fetching open feedbacks:', error);
        this.instantFeedback.set(undefined);
        this.instantFeedbacksDetail.set(undefined);
      },
    });
  }

  onCloseOpenFeedback() {
    this.displayOpenFeedbackDialog = false;
  }

  scheduleNextFeedbackDialog() {
    if (this.feedbackDialogTimeout) {
      clearTimeout(this.feedbackDialogTimeout);
    }
    this.feedbackDialogTimeout = setTimeout(() => {
      this.getAllOpenFeedbacks();
    }, 60000); // 1 minuto
  }

  // Mostra il SuccessModal solo quando la chiamata va a buon fine (evento submitFeedback)
  onSubmitInstantFeedback() {
    this.scheduleNextFeedbackDialog();
    this.showSuccessModal = true;
  }

  onCloseInstantFeedback() {
    this.instantFeedbacksDetail.set(undefined);
    this.scheduleNextFeedbackDialog();
  }

  onOpenInstantFeedback() {
    this.displayOpenFeedbackDialog = true;
    this.showSuccessModalFromOpenFeedback = false;
  }

  // Chiudendo il SuccessModal tramite la X non deve riaprirsi
  onCloseSuccessModal() {
    this.showSuccessModal = false;
    this.showSuccessModalFromOpenFeedback = false;
  }

  onChangeSidebarWidth(width: number): void {
    this.anchorBtnPx = `${width}px`;
  }
}
